import { post, postJsonLines } from 'bones/utils/voicebox/post';
import {
  StardogVoiceboxAnalysisRequest,
  StardogVoiceboxAnalysisRequestJson,
  StardogVoiceboxChartRequest,
  StardogVoiceboxChartRequestJson,
  StardogVoiceboxDataResponse,
  StardogVoiceboxDescriptionRequest,
  StardogVoiceboxDescriptionRequestJson,
  StardogVoiceboxDescriptionResponse,
  StardogVoiceboxDiagnosticsRequest,
  StardogVoiceboxDiagnosticsRequestJson,
  StardogVoiceboxDiagnosticsResponse,
  StardogVoiceboxFeedbackRequest,
  StardogVoiceboxFeedbackRequestJson,
  StardogVoiceboxGenerateStoredQueriesRequest,
  StardogVoiceboxGenerateStoredQueriesRequestJson,
  StardogVoiceboxMappingSuggestionsRequest,
  StardogVoiceboxMappingSuggestionsResponse,
  StardogVoiceboxMessageRequest,
  StardogVoiceboxMessageRequestJson,
  StardogVoiceboxMessageResponse,
  StardogVoiceboxMessageResponseJson,
} from 'bones/utils/voicebox/types';

const VOICEBOX_DIAGNOSTICS_ERROR_CODES = new Set([400]);

const VOICEBOX_DESCRIBE_DATASET_ERROR_CODES = new Set([]);

const VOICEBOX_FEEDBACK_ERROR_CODES = new Set([400, 404]);

const VOICEBOX_GENERATE_CHART_ERROR_CODES = new Set([400, 404]);

const VOICEBOX_GENERATE_STORED_QUERIES_ERROR_CODES = new Set([400, 404]);

const VOICEBOX_MAIN_ERROR_CODES = new Set([400, 404, 429]);

const VOICEBOX_MAPPING_SUGGESTIONS_ERROR_CODES = new Set([]);

export const requestStardogVoiceboxAnalysis = async ({
  connectionId,
  conversationId,
  messageId,
  context,
  analysis,
}: StardogVoiceboxAnalysisRequest) =>
  post<StardogVoiceboxAnalysisRequestJson, StardogVoiceboxDataResponse>(
    '/api/voicebox/v1/analyze',
    {
      connection_id: connectionId,
      conversation_id: conversationId,
      message_id: messageId,
      context,
      analysis,
    },
    VOICEBOX_GENERATE_CHART_ERROR_CODES
  );

export const requestStardogVoiceboxChart = async ({
  connectionId,
  conversationId,
  messageId,
  context,
  chartStyle,
}: StardogVoiceboxChartRequest) =>
  post<StardogVoiceboxChartRequestJson, StardogVoiceboxDataResponse>(
    '/api/voicebox/v1/generate-chart',
    {
      connection_id: connectionId,
      conversation_id: conversationId,
      message_id: messageId,
      context,
      chart_style: chartStyle || undefined,
    },
    VOICEBOX_GENERATE_CHART_ERROR_CODES
  );

export const requestStardogVoiceboxDescription = async ({
  connectionId,
  databaseId,
  model,
}: StardogVoiceboxDescriptionRequest) =>
  post<
    StardogVoiceboxDescriptionRequestJson,
    StardogVoiceboxDescriptionResponse
  >(
    '/api/voicebox/v1/describe-dataset',
    {
      connection_id: connectionId,
      database: databaseId,
      model,
    },
    VOICEBOX_DESCRIBE_DATASET_ERROR_CODES
  );

export const requestStardogVoiceboxDiagnostics = async ({
  connectionId,
  databaseId,
  model,
  checkAllSchemas,
  checkQueries,
}: StardogVoiceboxDiagnosticsRequest) =>
  post<
    StardogVoiceboxDiagnosticsRequestJson,
    StardogVoiceboxDiagnosticsResponse
  >(
    '/api/voicebox/v1/diagnostics',
    {
      connection_id: connectionId,
      database: databaseId,
      model,
      check_all_schemas: checkAllSchemas || !model,
      check_queries: checkQueries,
    },
    VOICEBOX_DIAGNOSTICS_ERROR_CODES
  );

export const requestStardogVoiceboxFeedback = async ({
  comment,
  messageId,
  score,
}: StardogVoiceboxFeedbackRequest) =>
  post<StardogVoiceboxFeedbackRequestJson, { ok: true }>(
    '/api/voicebox/v1/feedback',
    {
      message_id: messageId,
      comment: comment || undefined,
      score: score !== undefined ? score : undefined,
    },
    VOICEBOX_FEEDBACK_ERROR_CODES,
    { formatResponse: async () => ({ ok: true }) }
  );

export const requestStardogVoiceboxGenerateStoredQueries = async ({
  connectionId,
  databaseId,
  model,
}: StardogVoiceboxGenerateStoredQueriesRequest) =>
  post<StardogVoiceboxGenerateStoredQueriesRequestJson, { ok: true }>(
    '/api/voicebox/v1/generate-stored-queries',
    {
      connection_id: connectionId,
      database: databaseId,
      model,
    },
    VOICEBOX_GENERATE_STORED_QUERIES_ERROR_CODES,
    { formatResponse: async () => ({ ok: true }) }
  );

export const requestStardogVoiceboxMappingSuggestions = async ({
  connectionIdx,
  body,
}: StardogVoiceboxMappingSuggestionsRequest) =>
  post<string, StardogVoiceboxMappingSuggestionsResponse>(
    `/api/voicebox/v1/mapping-suggestions/${connectionIdx}`,
    body,
    VOICEBOX_MAPPING_SUGGESTIONS_ERROR_CODES,
    {
      formatResponse: async (response) => {
        const text = await response.text();
        return { results: JSON.parse(text) };
      },
      headers: {
        Accept: 'application/ld+json',
        'Content-Type': 'application/trig',
      },
    }
  );

export const requestStardogVoiceboxMessage = async (
  version: string,
  {
    app,
    conversationId,
    connectionId,
    context,
    query,
  }: StardogVoiceboxMessageRequest
) =>
  postJsonLines<
    StardogVoiceboxMessageRequestJson,
    StardogVoiceboxMessageResponseJson,
    StardogVoiceboxMessageResponse
  >(
    `/api/voicebox/${version}/main`,
    {
      app,
      context,
      query,
      connection_id: connectionId,
      conversation_id: conversationId || undefined,
    },
    VOICEBOX_MAIN_ERROR_CODES,
    {
      formatJsonResponse: (json) => ({
        actions: json.actions,
        conversationId: json.conversation_id,
        examples: json.followup_examples,
        messageId: json.message_id,
        result: json.result,
        pending: json.pending,
        success: json.success,
        type: json.message_type,
      }),
    }
  );
